import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ResponsiveContainer, BarChart, Bar, Cell, ComposedChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, ReferenceLine, Legend } from "recharts";
import { api } from "@/lib/api";
import { brl, pct, num, dm } from "@/lib/format";
import { Card, Segmented } from "@/components/kit";

const MODES = [{ value: "value", label: "Desvio em R$" }, { value: "pct", label: "CMV % teórico x real" }];
const AXIS = { fontSize: 11, fill: "var(--muted)" };
const nice = (v) => { const s = 10 ** Math.max(1, Math.floor(Math.log10(Math.abs(v) || 1))); return Math.ceil(Math.abs(v) / s) * s * Math.sign(v); };
const ZERO = [(min) => Math.min(0, nice(min)), (max) => Math.max(0, nice(max))];
const TIP = { borderRadius: 10, border: "1px solid var(--border)", background: "var(--surface)" };

function ValueChart({ data, onPick }) {
  return (
    <ResponsiveContainer width="100%" height="100%">
      <BarChart data={data} margin={{ left: 0, right: 8, top: 8 }} accessibilityLayer={false}>
        <CartesianGrid stroke="var(--border)" vertical={false} />
        <XAxis dataKey="name" tick={AXIS} axisLine={false} tickLine={false} />
        <YAxis tick={AXIS} axisLine={false} tickLine={false} width={72} domain={ZERO} tickFormatter={(v) => `R$ ${num(v, 0)}`} />
        <Tooltip formatter={(v) => [brl(v), "Desvio (real − teórico)"]} contentStyle={TIP} cursor={{ fill: "var(--surface-alt)" }} />
        <ReferenceLine y={0} stroke="var(--border)" />
        <Bar dataKey="deviation" maxBarSize={56} radius={[6, 6, 0, 0]} onClick={(d) => onPick(d.conference_id)} cursor="pointer">
          {data.map((d) => <Cell key={d.conference_id} fill={d.deviation > 0 ? "var(--danger)" : "var(--success)"} />)}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}

function PctChart({ data, onPick }) {
  const label = { theoretical_pct: "CMV teórico", real_pct: "CMV real", deviation_pp: "Desvio (p.p.)" };
  return (
    <ResponsiveContainer width="100%" height="100%">
      <ComposedChart data={data} margin={{ left: -12, right: 8, top: 8 }} accessibilityLayer={false} onClick={(e) => { const i = Number(e?.activeTooltipIndex ?? e?.activeIndex); if (data[i]) onPick(data[i].conference_id); }}>
        <CartesianGrid stroke="var(--border)" vertical={false} />
        <XAxis dataKey="name" tick={AXIS} axisLine={false} tickLine={false} />
        <YAxis tick={AXIS} axisLine={false} tickLine={false} unit="%" domain={ZERO} />
        <Tooltip formatter={(v, k) => [k === "deviation_pp" ? `${num(v, 1)} p.p.` : pct(v), label[k]]} contentStyle={TIP} />
        <Legend formatter={(k) => label[k]} wrapperStyle={{ fontSize: 12 }} />
        <Bar dataKey="deviation_pp" fill="var(--accent)" radius={[5, 5, 0, 0]} barSize={18} cursor="pointer" />
        <Line type="monotone" dataKey="theoretical_pct" stroke="var(--brand)" strokeWidth={2.5} dot={{ r: 3 }} />
        <Line type="monotone" dataKey="real_pct" stroke="var(--danger)" strokeWidth={2.5} dot={{ r: 3 }} />
      </ComposedChart>
    </ResponsiveContainer>
  );
}

export default function WeeklyChart({ onPick }) {
  const [mode, setMode] = useState("value");
  const { data = [] } = useQuery({ queryKey: ["theoretical-weekly"], queryFn: () => api("/cmv-analysis/weekly") });
  const chart = data.map((d) => ({ ...d, name: dm(d.week_start) }));
  return (
    <Card className="flex flex-col gap-4" data-testid="weekly-deviation-chart">
      <div className="flex flex-wrap items-center gap-3">
        <div className="h3 flex-1">Desvio por conferência</div>
        <Segmented options={MODES} value={mode} onChange={setMode} testid="weekly-chart-mode" />
      </div>
      {chart.length < 1 ? <p className="caption" data-testid="weekly-chart-empty">Importe as vendas de pelo menos uma conferência para ver a evolução.</p> : (
        <>
          <div className="h-64" data-testid={`weekly-chart-${mode}`}>{mode === "value" ? <ValueChart data={chart} onPick={onPick} /> : <PctChart data={chart} onPick={onPick} />}</div>
          <p className="caption">{chart.length} conferência(s) com vendas importadas · toque em uma para abrir os detalhes.{chart.length === 1 ? " Importe mais conferências para ver a tendência." : ""}</p>
        </>
      )}
    </Card>
  );
}
